import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney } from "@/lib/api";
import { Trophy, Flame, FileSignature, DollarSign, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
const daysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

const lastActiveLabel = (iso) => {
  const d = daysSince(iso);
  if (d === null) return "Never";
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d < 30) return `${d} days ago`;
  return new Date(iso).toLocaleDateString();
};

const PERIODS = [
  { id: 0, label: "All time" },
  { id: 30, label: "30 days" },
  { id: 7, label: "7 days" },
];

const COLS = [
  { key: "calls_made", label: "Calls" },
  { key: "texts_sent", label: "Texts" },
  { key: "leads_generated", label: "Leads" },
  { key: "appointments_set", label: "Appts" },
  { key: "offers_made", label: "Offers" },
  { key: "contracts_signed", label: "Contracts" },
  { key: "deals_closed", label: "Closed" },
];

export default function AdminKpis() {
  const navigate = useNavigate();
  const [quietOnly, setQuietOnly] = useState(false);
  const [period, setPeriod] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const reload = () => {
    setLoading(true);
    const params = period ? { days: period } : {};
    return api.get("/admin/kpis/leaderboard", { params })
      .then(({ data }) => setRows(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = period ? { days: period } : {};
    api.get("/admin/kpis/leaderboard", { params })
      .then(({ data }) => { if (active) setRows(data); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [period]);

  const deleteStudent = async () => {
    if (!confirmDelete) return;
    try {
      const { data } = await api.delete(`/admin/students/${confirmDelete.user_id}`);
      toast.success(`Removed ${data.deleted_email} (${data.deals_removed} deal${data.deals_removed === 1 ? "" : "s"})`);
      setConfirmDelete(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const quietRows = useMemo(() => {
    const d = (r) => daysSince(r.last_active);
    return rows.filter((r) => d(r) === null || d(r) >= 7);
  }, [rows]);

  const visibleRows = quietOnly ? quietRows : rows;

  const agg = useMemo(() => {
    const activeStudents = rows.filter((r) => (r.total_activity || 0) > 0).length;
    const offers = rows.reduce((s, r) => s + (r.offers_made || 0), 0);
    const contracts = rows.reduce((s, r) => s + (r.contracts_signed || 0), 0);
    const profit = rows.reduce((s, r) => s + (r.profit_earned || 0), 0);
    return { activeStudents, offers, contracts, profit };
  }, [rows]);

  return (
    <div data-testid="admin-kpis-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-tag mb-3"><span className="dot" /> Who&apos;s taking action</div>
          <h1 className="h-display text-4xl">Student KPIs</h1>
          <p className="text-sm mt-3" style={{ color: "var(--text-muted)" }}>
            Every number students have logged, all time by default. Nothing resets.
          </p>
          <p className="text-xs mt-2" style={{ color: "var(--text-dim)" }}>
            Last active counts any time a student opens the portal. It starts from today, so anyone who has
            not been back yet shows Never.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setQuietOnly((v) => !v)}
            className="px-3 py-1.5 rounded-full text-xs font-medium transition"
            style={{
              background: quietOnly ? "var(--lime-glow)" : "var(--surface2)",
              color: quietOnly ? "var(--lime)" : "var(--text-muted)",
              border: quietOnly ? "1px solid var(--border-lime)" : "1px solid var(--border)",
            }}
            title="Students who have not opened the app in 7 days or more"
            data-testid="admin-quiet-filter"
          >
            Quiet 7+ days ({quietRows.length})
          </button>
        </div>
        <div className="flex gap-2" data-testid="admin-kpi-period-tabs">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition"
              style={{
                background: period === p.id ? "var(--lime-glow)" : "var(--surface2)",
                color: period === p.id ? "var(--lime)" : "var(--text-muted)",
                border: period === p.id ? "1px solid var(--border-lime)" : "1px solid var(--border)",
              }}
              data-testid={`admin-kpi-period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-kpi-stats">
        <TopCard icon={Flame} label="Active students" val={agg.activeStudents} accent />
        <TopCard icon={FileSignature} label="Offers made" val={agg.offers} />
        <TopCard icon={Trophy} label="Contracts signed" val={agg.contracts} />
        <TopCard icon={DollarSign} label="Student profit" val={fmtMoney(agg.profit)} />
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><span className="spinner" /></div>
      ) : rows.length === 0 ? (
        <div className="surface-card p-10 text-center text-sm" style={{ color: "var(--text-dim)" }}>
          No students yet.
        </div>
      ) : (
        <div className="surface-card p-6" data-testid="admin-kpi-leaderboard">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-4">Student</th>
                  {COLS.map((c) => <th key={c.key} className="py-2 pr-4 whitespace-nowrap">{c.label}</th>)}
                  <th className="py-2 pr-4 whitespace-nowrap">Profit</th>
                  <th className="py-2 pr-4 whitespace-nowrap">Deals on Portal</th>
                  <th className="py-2 pr-4 whitespace-nowrap">Streak</th>
                  <th className="py-2 pr-4 whitespace-nowrap">Last Log</th>
                  <th className="py-2 pr-4 whitespace-nowrap">Last Active</th>
                  <th className="py-2 whitespace-nowrap"></th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, i) => (
                  <tr
                    key={r.user_id}
                    className="transition hover:bg-white/[0.03]"
                    style={{ borderTop: "1px solid var(--border)" }}
                    data-testid={`leaderboard-row-${i}`}
                  >
                    <td className="py-3 pr-3 font-mono text-xs cursor-pointer" onClick={() => navigate(`/admin/students/${r.user_id}`)} style={{ color: i < 3 && (r.total_activity || 0) > 0 ? "var(--lime)" : "var(--text-dim)" }}>
                      {i + 1}
                    </td>
                    <td className="py-3 pr-4 cursor-pointer" onClick={() => navigate(`/admin/students/${r.user_id}`)}>
                      <div className="font-medium" style={{ color: "var(--text)" }}>{r.name}</div>
                      <div className="font-mono text-[10px]" style={{ color: "var(--text-dim)" }}>{r.email}</div>
                    </td>
                    {COLS.map((c) => (
                      <td key={c.key} onClick={() => navigate(`/admin/students/${r.user_id}`)} className="py-3 pr-4 font-mono text-xs cursor-pointer" style={{ color: (r[c.key] || 0) > 0 ? "var(--text)" : "var(--text-dim)" }}>
                        {(r[c.key] || 0).toLocaleString()}
                      </td>
                    ))}
                    <td onClick={() => navigate(`/admin/students/${r.user_id}`)} className="py-3 pr-4 font-mono text-xs cursor-pointer" style={{ color: (r.profit_earned || 0) > 0 ? "var(--lime)" : "var(--text-dim)" }}>
                      {fmtMoney(r.profit_earned || 0)}
                    </td>
                    <td onClick={() => navigate(`/admin/students/${r.user_id}`)} className="py-3 pr-4 font-mono text-xs cursor-pointer" style={{ color: "var(--text-muted)" }}>
                      {r.deals_submitted} / {r.deals_closed_platform} closed
                    </td>
                    <td onClick={() => navigate(`/admin/students/${r.user_id}`)} className="py-3 pr-4 font-mono text-xs whitespace-nowrap cursor-pointer">
                      {r.weekly_streak > 0 ? (
                        <span className="inline-flex items-center gap-1" style={{ color: "#ff8c32" }}>
                          <Flame size={12} fill="#ff8c32" /> {r.weekly_streak} wk
                        </span>
                      ) : r.daily_streak > 0 ? (
                        <span className="inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                          <Flame size={12} /> {r.daily_streak} d
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-dim)" }}>—</span>
                      )}
                    </td>
                    <td onClick={() => navigate(`/admin/students/${r.user_id}`)} className="py-3 pr-4 font-mono text-xs cursor-pointer" style={{ color: "var(--text-dim)" }}>{r.last_entry || "—"}</td>
                    <td
                      onClick={() => navigate(`/admin/students/${r.user_id}`)}
                      className="py-3 font-mono text-xs whitespace-nowrap cursor-pointer"
                      style={{ color: (daysSince(r.last_active) ?? 99) >= 7 ? "var(--red)" : "var(--text-muted)" }}
                      data-testid={`leaderboard-last-active-${i}`}
                    >
                      {lastActiveLabel(r.last_active)}
                    </td>
                    <td className="py-3 pl-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete({ user_id: r.user_id, name: r.name, email: r.email }); }}
                        className="p-1.5 rounded-lg hover:bg-white/5 transition"
                        style={{ color: "#f87171" }}
                        title="Delete student"
                        data-testid={`leaderboard-delete-${i}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title={`Remove ${confirmDelete?.name || confirmDelete?.email}?`}
        description={`This permanently deletes the student account (${confirmDelete?.email}), their KPI entries, deals, leads, support tickets, and notifications. Cannot be undone.`}
        destructive
        confirmLabel="Delete Student"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={deleteStudent}
      />
    </div>
  );
}

function TopCard({ icon: Icon, label, val, accent }) {
  return (
    <div className="surface-card p-4" style={accent ? { borderColor: "var(--border-lime)" } : undefined}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} style={{ color: "var(--lime)" }} />
        <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</span>
      </div>
      <div className="font-mono text-2xl" style={{ color: accent ? "var(--lime)" : "var(--text)" }}>{val}</div>
    </div>
  );
}
